import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Form,
  Input,
  Select,
  Typography,
  Textarea,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { Play, Plus, X } from 'lucide-react';
import { remiSetupSchema, RemiSetupData } from '../schemas/onboarding.schema';
import { HttpsUrlInput } from '../../../common/HttpsUrlInput';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { InfoNotice } from '../components/InfoNotice';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { useAiVoices } from '../../../../hooks/useAiVoices';
import { defaultVoiceSelection, normalizeVoiceSelection } from '../../../../hooks/voiceSelection';
import { useVoiceSample } from '../../../../hooks/useVoiceSample';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';

interface RemiSetupStepProps {
  onNext: (data: RemiSetupData) => void;
  initialData?: Partial<RemiSetupData>;
  isLoading?: boolean;
  organizationName?: string;
}

const BACKGROUND_OPTIONS = [
  { value: 'none', label: 'No Background' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'office', label: 'Office' },
  { value: 'restaurant', label: 'Restaurant' },
];

export const RemiSetupStep: React.FC<RemiSetupStepProps> = ({
  onNext,
  initialData,
  isLoading = false,
  organizationName = '',
}) => {
  const { data: voices = [] } = useAiVoices();
  const { playVoiceSample, isGenerating, isPlaying } = useVoiceSample('staging');
  const { showToast } = useToast();
  const currentUser = useOrganizationStore((state) => state.currentUser);

  // Until the client edits the pronunciation, it follows the assistant name.
  const pronunciationEdited = useRef(Boolean(initialData?.assistant_name_pronunciation));

  const form = useForm<RemiSetupData>({
    resolver: zodResolver(remiSetupSchema),
    defaultValues: {
      remi_intro_name: initialData?.remi_intro_name || '',
      assistant_name: initialData?.assistant_name || 'Remi',
      // Starts as the name itself; most names are said the way they're spelled.
      assistant_name_pronunciation:
        initialData?.assistant_name_pronunciation || initialData?.assistant_name || 'Remi',
      voice: initialData?.voice || '',
      background_track: initialData?.background_track || '',
      kb_name: initialData?.kb_name || (organizationName ? `${organizationName} FAQ` : ''),
      service_type: initialData?.service_type || '',
      service_areas: initialData?.service_areas || '',
      why_work_with_you: initialData?.why_work_with_you || '',
      website_url: initialData?.website_url || '',
      custom_questions: initialData?.custom_questions || [],
    },
  });

  useEffect(() => {
    const storedVoice = form.getValues('voice') || initialData?.voice;
    if (storedVoice) {
      form.setValue('voice', normalizeVoiceSelection(storedVoice, voices));
      return;
    }
    const catalogDefault = defaultVoiceSelection(voices);
    if (catalogDefault) {
      form.setValue('voice', catalogDefault, { shouldValidate: true });
    }
  }, [form, initialData?.voice, voices]);

  const remiIntroName = form.watch('remi_intro_name');

  const handleSubmit = async (data: RemiSetupData) => {
    // Don't send webhook here - will send after Twilio step is complete
    onNext(data);
  };

  const handlePlayVoice = async (voiceId: string) => {
    if (!voiceId) {
      showToast({
        message: 'Please select a voice first',
        variant: 'error',
      });
      return;
    }

    const selectedVoice = voices.find((v) => v.voice_id === voiceId);
    const voiceName = selectedVoice?.name || 'Assistant';

    const userFirstName = currentUser?.displayName?.split(' ')[0] || 'there';
    let greetingText = `Hi ${userFirstName}, this is ${voiceName} your personal assistant. I can call new leads, schedule follow-ups, and reconnect with your dormant contacts so you can focus on selling homes`;

    if (greetingText.length > 200) {
      greetingText = `Hi ${userFirstName}, this is your personal assistant. I call new leads, follow up with contacts, and help grow your business while you focus on closing deals.`;
    }

    try {
      await playVoiceSample(voiceId, greetingText);
      showToast({
        message: 'Playing voice sample...',
        variant: 'success',
      });
    } catch (error) {
      console.error('Voice sample error:', error);
      showToast({
        message: 'Failed to play voice sample. Please try again.',
        variant: 'error',
      });
    }
  };

  const voiceOptions = voices
    .map((voice) => {
      const id = voice.voice_id;
      const baseLabel = voice.displayLabel || `${voice.name} - ${voice.gender}`;
      return {
        value: id,
        label: baseLabel,
        isRecommended: voice.isRecommended === true,
      };
    })
    // Surface the recommended voice at the top of the list
    .sort((a, b) => Number(b.isRecommended) - Number(a.isRecommended));

  const customQuestions = form.watch('custom_questions') || [];

  const addCustomQuestion = () => {
    const current = form.getValues('custom_questions') || [];
    form.setValue('custom_questions', [...current, { question: '', answer: '' }]);
  };

  const removeCustomQuestion = (index: number) => {
    const current = form.getValues('custom_questions') || [];
    form.setValue(
      'custom_questions',
      current.filter((_, i) => i !== index)
    );
  };

  return (
    <StageContainer
      title="Remi Voice & Knowledge Setup"
      description="Configure how Remi sounds and what Remi knows about your business. This helps create a personalized experience for your leads and clients."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8" noValidate>
        {/* REMI VOICE SETUP */}
        <div className="space-y-6">
          <Typography variant="h2" className="text-xl font-bold text-gray-900 border-b pb-2">
            Remi Voice Setup
          </Typography>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>How should Remi introduce your company?</QuestionLabel>
              <Input
                {...form.register('remi_intro_name')}
                placeholder="ABC Realty"
                error={form.formState.errors.remi_intro_name?.message}
              />
              <TipBox>
                {remiIntroName
                  ? `Hi, I'm Remi, the Digital Assistant with ${remiIntroName}.`
                  : 'This is how Remi will introduce your company name when talking to leads (e.g., "Hi, I\'m Remi, the Digital Assistant with ____________.")'}
              </TipBox>
            </Form.Control>
          </FormSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control required>
                <QuestionLabel>What is your assistant name?</QuestionLabel>
                <Input
                  {...form.register('assistant_name', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (!pronunciationEdited.current) {
                        form.setValue('assistant_name_pronunciation', e.target.value, {
                          shouldValidate: form.formState.isSubmitted,
                        });
                      }
                    },
                  })}
                  placeholder="Remi"
                  error={form.formState.errors.assistant_name?.message}
                />
                <TipBox>
                  This is the name your assistant will use when introducing themselves to leads.
                </TipBox>
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control required>
                <QuestionLabel>How do you pronounce the name?</QuestionLabel>
                <Input
                  {...form.register('assistant_name_pronunciation', {
                    onChange: () => {
                      pronunciationEdited.current = true;
                    },
                  })}
                  error={form.formState.errors.assistant_name_pronunciation?.message}
                />
                <TipBox>
                  Leave as-is unless the name is pronounced differently than it&apos;s spelled.
                </TipBox>
              </Form.Control>
            </FormSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control required>
                <QuestionLabel>Choose voice</QuestionLabel>
                <div className="flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <Controller
                      control={form.control}
                      name="voice"
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={voiceOptions}
                          placeholder="Select a voice"
                          error={form.formState.errors.voice?.message}
                          searchable
                        />
                      )}
                    />
                  </div>
                  {form.watch('voice') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="md"
                      onClick={() => handlePlayVoice(form.watch('voice'))}
                      disabled={isGenerating || isPlaying}
                      className="inline-flex items-center justify-center flex-shrink-0  h-8"
                    >
                      {isGenerating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-t-transparent" />
                      ) : isPlaying ? (
                        <div className="flex items-center gap-1">
                          <div
                            className="w-1 h-3 bg-yellow-600 animate-pulse rounded-full"
                            style={{ animationDelay: '0ms', animationDuration: '600ms' }}
                          />
                          <div
                            className="w-1 h-4 bg-yellow-600 animate-pulse rounded-full"
                            style={{ animationDelay: '150ms', animationDuration: '600ms' }}
                          />
                          <div
                            className="w-1 h-3 bg-yellow-600 animate-pulse rounded-full"
                            style={{ animationDelay: '300ms', animationDuration: '600ms' }}
                          />
                        </div>
                      ) : (
                        <Play className="w-4 h-4 text-yellow-700 dark:text-yellow-400" />
                      )}
                    </Button>
                  )}
                </div>
                <TipBox>Click Preview to hear how this voice sounds before selecting.</TipBox>
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control>
                <QuestionLabel required={false}>Background audio</QuestionLabel>
                <Controller
                  control={form.control}
                  name="background_track"
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={BACKGROUND_OPTIONS}
                      placeholder="Select background audio"
                      error={form.formState.errors.background_track?.message}
                    />
                  )}
                />
                <TipBox>
                  Add subtle background audio to make calls feel more natural. Select "No
                  Background" for a clean audio experience.
                </TipBox>
              </Form.Control>
            </FormSection>
          </div>
        </div>

        {/* REMI KNOWLEDGE BASE SETUP */}
        <div className="space-y-6">
          <div>
            <Typography variant="h2" className="text-xl font-bold text-gray-900 border-b pb-2 mb-3">
              Knowledge Base Setup
            </Typography>
            <InfoNotice>
              A knowledge base is a simple, organized collection of your company's most important
              information—everything your team and AI need to know to operate consistently. It
              includes who you are, what you offer, how your processes work, and how you
              communicate. A great knowledge base is clear, easy to read, regularly updated, and
              written as if you're explaining your business to someone brand new. Its purpose is to
              ensure consistency, save time, and help your AI sound and perform exactly the way you
              want.
            </InfoNotice>
          </div>

          {/* Required Questions */}
          <FormSection>
            <Form.Control required>
              <QuestionLabel>What type of service do you provide?</QuestionLabel>
              <Textarea
                {...form.register('service_type')}
                placeholder="e.g., We provide full-service real estate services including buying, selling, and property management..."
                error={form.formState.errors.service_type?.message}
                rows={3}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>What areas do you service?</QuestionLabel>
              <Textarea
                {...form.register('service_areas')}
                placeholder="e.g., We serve the greater San Francisco Bay Area, including San Francisco, Oakland, and surrounding cities..."
                error={form.formState.errors.service_areas?.message}
                rows={3}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Why should a lead work with you?</QuestionLabel>
              <Textarea
                {...form.register('why_work_with_you')}
                placeholder="e.g., Our team has over 20 years of local market experience and we provide personalized service..."
                error={form.formState.errors.why_work_with_you?.message}
                rows={3}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control>
              <QuestionLabel required>What is your website URL?</QuestionLabel>
              <HttpsUrlInput
                {...form.register('website_url')}
                placeholder="yourcompany.com"
                error={form.formState.errors.website_url?.message}
              />
              <TipBox>This will help Remi direct leads to your website when asked.</TipBox>
            </Form.Control>
          </FormSection>

          {/* Custom Questions */}
          {customQuestions.length > 0 && (
            <div className="space-y-4">
              <Typography variant="h3" className="text-lg font-semibold">
                Custom Questions
              </Typography>
              {customQuestions.map((_, index) => (
                <FormSection key={index}>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700">
                        Question {index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeCustomQuestion(index)}
                        className="inline-flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Remove
                      </Button>
                    </div>
                    <Input
                      {...form.register(`custom_questions.${index}.question`)}
                      placeholder="Enter your question"
                    />
                    <Textarea
                      {...form.register(`custom_questions.${index}.answer`)}
                      placeholder="Enter the answer"
                      rows={2}
                    />
                  </div>
                </FormSection>
              ))}
            </div>
          )}

          <Button
            type="button"
            variant="secondary"
            onClick={addCustomQuestion}
            className="inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Custom Question
          </Button>

          <TipBox>
            Knowledge base can be updated at any time to add new context to Remi about your
            team/company.
          </TipBox>
        </div>

        {/* Submit Button */}
        <NextButton isLoading={isLoading} />
      </form>
    </StageContainer>
  );
};
